// Media-type rules for digital price sheet products, shared by React tables and Angular panels.

export type MediaType = NonNullable<SpApi.Studio.IPriceSheetDigitalItem['mediaType']>;

export type VideoResolution = NonNullable<SpApi.Studio.IPriceSheetDigitalItem['videoResolution']>;

export type DownloadScope = 'allAlbum' | 'allGallery' | 'individual';

type Translate = (key: string, interpolateValues?: Record<string, unknown>) => string;

type LooseMediaType = Nullable<string> | undefined;

// Loose enough for the untyped item lists the Angular panels pass in.
type DigitalLike = {
    mediaType?: Nullable<string>;
    videoResolution?: Nullable<string>;
};

const PHOTO: MediaType = 'photo';
const SLIDESHOW: MediaType = 'slideshow';

const ORIGINAL: VideoResolution = 'Original';
const RESOLUTION_1080P: VideoResolution = '1080p';

const FULL_RESOLUTION = 'full-resolution';
const CUSTOM_RESOLUTION = 'custom';

// Mirrors Sp_PriceSheet_Item::VIDEO_RESOLUTIONS_BY_MEDIA_TYPE.
const VIDEO_RESOLUTIONS_BY_MEDIA_TYPE: { [mediaType in MediaType]: VideoResolution[] } = {
    photo: [],
    video: [ORIGINAL, RESOLUTION_1080P],
    slideshow: [RESOLUTION_1080P]
};

// Mirrors Sp_PriceSheet_Item::DEFAULT_VIDEO_RESOLUTION_BY_MEDIA_TYPE.
const DEFAULT_VIDEO_RESOLUTION_BY_MEDIA_TYPE: {
    [mediaType in MediaType]: Nullable<VideoResolution>;
} = {
    photo: null,
    video: ORIGINAL,
    slideshow: RESOLUTION_1080P
};

// The noun a media type is called in translation copy - "photo" is "digital", a naming holdover.
const MEDIA_TYPE_NOUN: { [mediaType in MediaType]: string } = {
    photo: 'digital',
    slideshow: 'slideshow',
    video: 'video'
};

const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

const pluralNoun = (mediaType: MediaType): string => `${capitalize(MEDIA_TYPE_NOUN[mediaType])}s`;

// Shared namespace so label copy lives once rather than duplicated per screen.
const DIGITAL_MEDIA_TYPE_NAMESPACE = 'studio.priceSheet.digitalMediaType';

const DOWNLOAD_TYPE_LABEL_KEY: { [downloadType: string]: string } = {
    [FULL_RESOLUTION]: 'originalResolution',
    'high-resolution': 'highResolution',
    'medium-resolution': 'midResolution',
    'low-resolution': 'lowResolution'
};

// Digital subgroup bounds, from Sp_PriceSheet_Item::generateSubgroupForDigitalItem().
const MEDIA_TYPE_BY_SUBGROUP_BOUNDS: { [bounds: number]: MediaType } = {
    1: 'photo',
    3: 'video',
    4: 'slideshow'
};

const SELF_FULFILLED_SUBGROUP_BOUNDS = 2;

// Self-fulfilled and pre-existing digitals carry no mediaType, so absent means photo.
export const getMediaType = (mediaType: LooseMediaType): MediaType =>
    mediaType === 'video' || mediaType === 'slideshow' ? mediaType : PHOTO;

export const isVideoOrSlideshow = (mediaType: LooseMediaType): boolean =>
    getMediaType(mediaType) !== PHOTO;

export const isSlideshow = (mediaType: LooseMediaType): boolean =>
    getMediaType(mediaType) === SLIDESHOW;

// The singular noun a media type is called in translation copy - e.g. "Video" - for callers
// building their own copy outside the label functions below.
export const getMediaTypeNoun = (mediaType: LooseMediaType): string =>
    capitalize(MEDIA_TYPE_NOUN[getMediaType(mediaType)]);

export const getVideoResolutions = (mediaType: LooseMediaType): VideoResolution[] =>
    VIDEO_RESOLUTIONS_BY_MEDIA_TYPE[getMediaType(mediaType)];

export const getDefaultVideoResolution = (mediaType: LooseMediaType): Nullable<VideoResolution> =>
    DEFAULT_VIDEO_RESOLUTION_BY_MEDIA_TYPE[getMediaType(mediaType)];

// False for a photo, whose original size is a digitalDownloadType rather than a videoResolution.
export const supportsOriginalResolution = (mediaType: LooseMediaType): boolean =>
    getVideoResolutions(mediaType).includes(ORIGINAL);

// Video and slideshow products are gated behind ?new_products=1 while ADO 81770 rolls out.
export const areVideoAndSlideshowProductsEnabled = (): boolean =>
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('new_products') === '1';

// Whether a product of the same media type already sells this resolution.
export const isVideoResolutionUsed = (
    digitalsList: Nullable<DigitalLike[]> | undefined,
    mediaType: LooseMediaType,
    resolution: VideoResolution
): boolean =>
    (digitalsList ?? []).some(
        (digital) =>
            getMediaType(digital.mediaType) === getMediaType(mediaType) &&
            digital.videoResolution === resolution
    );

// Whether the price sheet sells this media type at every resolution it supports.
export const areAllVideoResolutionsUsed = (
    digitalsList: Nullable<DigitalLike[]> | undefined,
    mediaType: LooseMediaType
): boolean =>
    isVideoOrSlideshow(mediaType) &&
    getVideoResolutions(mediaType).every((resolution) =>
        isVideoResolutionUsed(digitalsList, mediaType, resolution)
    );

export const getItemTypeLabel = (
    isDigitalDownload: boolean | undefined,
    mediaType: LooseMediaType,
    translate: Translate
): string =>
    isDigitalDownload
        ? translate(`${DIGITAL_MEDIA_TYPE_NAMESPACE}.itemTypeDownload`, {
              mediaTypeNoun: getMediaTypeNoun(mediaType)
          })
        : translate('selfFulfilled');

export const getGroupLabel = (
    isSelfFulfilled: boolean,
    mediaType: LooseMediaType,
    translate: Translate
): string =>
    isSelfFulfilled
        ? translate('selfFulfilledDigitals')
        : translate(`${DIGITAL_MEDIA_TYPE_NAMESPACE}.shootProofFulfilledGroup`, {
              mediaTypeNoun: pluralNoun(getMediaType(mediaType))
          });

export const getSubgroupLabel = (
    bounds: Nullable<string> | number | undefined,
    firstItem:
        | Pick<SpApi.Studio.IPriceSheetDigitalItem, 'isDigitalDownload' | 'mediaType'>
        | undefined,
    translate: Translate
): string => {
    if (firstItem) {
        return getGroupLabel(!firstItem.isDigitalDownload, firstItem.mediaType, translate);
    }

    const numericBounds = Number(bounds);

    if (numericBounds === SELF_FULFILLED_SUBGROUP_BOUNDS) {
        return translate('selfFulfilledDigitals');
    }

    return getGroupLabel(false, MEDIA_TYPE_BY_SUBGROUP_BOUNDS[numericBounds] ?? PHOTO, translate);
};

export const getDownloadScopeLabel = (
    scope: DownloadScope,
    mediaType: LooseMediaType,
    translate: Translate
): string => {
    const suffix = scope === 'individual' ? 'individualLabel' : `${scope}DownloadLabel`;
    const mediaTypeNoun = `${capitalize(getMediaType(mediaType))}s`;

    return translate(`${DIGITAL_MEDIA_TYPE_NAMESPACE}.${suffix}`, { mediaTypeNoun });
};

export const getResolutionLabel = (
    item: Pick<
        SpApi.Studio.IPriceSheetDigitalItem,
        'digitalDownloadType' | 'mediaType' | 'pixels' | 'videoResolution'
    >,
    translate: Translate
): string => {
    // A video/slideshow stores pixels: null, so only its rendition says what it delivers.
    if (isVideoOrSlideshow(item.mediaType)) {
        if (!item.videoResolution) {
            return '';
        }

        return item.videoResolution === ORIGINAL
            ? translate(DOWNLOAD_TYPE_LABEL_KEY[FULL_RESOLUTION])
            : item.videoResolution;
    }

    if (item.digitalDownloadType === CUSTOM_RESOLUTION) {
        return `${item.pixels} px`;
    }

    const labelKey = item.digitalDownloadType && DOWNLOAD_TYPE_LABEL_KEY[item.digitalDownloadType];

    return labelKey ? translate(labelKey) : '';
};
